import { Stack, styled } from '@mui/material'
import { CreationNavButton, PageGridContainer, PageGridItem, TinyButton, tokens, WhiteEmptyStateImage } from 'ComponentLibrary'
import { getFinalisedValuationIds, getFinalisedValuations, useAssetValuationList } from './AssetValuationList.data'
import AssetValuationListItem from './AssetValuationListItem'
import AssetValuationListLoading from './AssetValuationList.load'
import { isEmpty } from 'lodash'
import { Typography } from 'ComponentLibrary/src/piazza'
import { DownloadSimpleIcon, MicrosoftExcelLogoIcon } from '@phosphor-icons/react'
import LoadingIndicator from 'ComponentLibrary/src/atoms/LoadingIndicator/LoadingIndicator'
import { useState } from 'react'
import { ValXmlDownloadModal } from 'ClientPortal/src/common/modals/ValXmlDownloadModal'
import { useDownloadValXmls } from 'ClientPortal/src/api/Queries/valuations'
import { downloadFileFromLink } from 'ClientPortal/src/common/utils/files'
import { useTranslation } from 'react-i18next'

export default function AssetValuationList() {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tActions } = useTranslation('terminology', { keyPrefix: 'common.actions' })
    const { t: tExport } = useTranslation('terminology', { keyPrefix: 'export' })
    const { t: tAssetValuationList } = useTranslation('pages', { keyPrefix: 'assets.assetDetails.assetValuationList' })
    const {
        appraiserUsers,
        ordersWithValuations,
        isValuationsListPending,
        assetId,
        downloadAllReports,
        isGeneratingAllReportsZipUrl,
        hasAtLeastOneExportableValuation,
    } = useAssetValuationList()
    const [isValXmlsModalOpen, setIsValXmlsModalOpen] = useState(false)
    const { downloadValXmls, isDownloadingValXmls } = useDownloadValXmls({ assetId, onSuccess: downloadFileFromLink })
    const finalisedValuationIds = getFinalisedValuationIds(ordersWithValuations)
    const finalisedValuations = getFinalisedValuations(ordersWithValuations)

    if (isValuationsListPending) {
        return <AssetValuationListLoading />
    }

    return (
        <PageGridContainer>
            <PageGridItem columnSpan={6} columnSpanXs={4}>
                <Stack direction="column" gap={tokens.SizingSpacing300}>
                    <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center" justifyContent="flex-end">
                        <Typography variant="textS" color={tokens.ColorTextDefault}>
                            {tExport('sectionTitle')}
                        </Typography>
                        {!isEmpty(ordersWithValuations) && (
                            <TinyButton
                                leftIcon={isGeneratingAllReportsZipUrl ? <LoadingIndicator isPending /> : <DownloadSimpleIcon />}
                                label={tCommon('allReports')}
                                onClick={() => downloadAllReports()}
                            />
                        )}
                        {finalisedValuationIds.length > 0 && hasAtLeastOneExportableValuation && (
                            <TinyButton
                                leftIcon={<MicrosoftExcelLogoIcon />}
                                rightIcon={<DownloadSimpleIcon />}
                                label={tCommon('allValXmls')}
                                onClick={() => setIsValXmlsModalOpen(true)}
                            />
                        )}
                        <CreationNavButton label={tActions('newOrder')} href={`/assets/${assetId}/orders/new`} color="royal" />
                    </Stack>

                    {!isEmpty(ordersWithValuations) ? (
                        <ListGridContainer>
                            {ordersWithValuations.map(({ valuation, valueCheck, order, parentValuationStatus }) => (
                                <AssetValuationListItem
                                    key={valueCheck?.uuid ?? valuation?.uuid ?? order?.uuid}
                                    valuation={valuation}
                                    valueCheck={valueCheck}
                                    order={order}
                                    parentValuationStatus={parentValuationStatus}
                                    appraiserUsers={appraiserUsers}
                                />
                            ))}
                        </ListGridContainer>
                    ) : (
                        <Stack
                            direction="column"
                            gap={tokens.SizingSpacing200}
                            height="264px"
                            alignItems="center"
                            justifyContent="center"
                            paddingTop={tokens.SizingSpacing200}
                        >
                            <WhiteEmptyStateImage height="120px" width="145px" />
                            <Typography variant="textS" color={tokens.ColorTextDefault}>
                                {tAssetValuationList('emptyStateTitle')}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </PageGridItem>
            <ValXmlDownloadModal
                isOpen={isValXmlsModalOpen}
                onClose={() => setIsValXmlsModalOpen(false)}
                onDownload={async () => {
                    await downloadValXmls()
                    setIsValXmlsModalOpen(false)
                }}
                isDownloading={isDownloadingValXmls}
                mode="multiple"
                hasValuationsWhichCannotBeExportedToValXML={
                    !finalisedValuations.map((valuation) => valuation.capabilities?.valxml_exportable.valxml_v1_1).every(Boolean)
                }
            />
        </PageGridContainer>
    )
}

const ListGridContainer = styled('div')(() => ({
    display: 'grid',
    rowGap: tokens.SizingSpacing100,
    columnGap: tokens.SizingSpacing150,
    gridTemplateColumns: '2.125fr repeat(3, auto) 1fr 1fr auto',
    overflow: 'auto',
    paddingBottom: tokens.SizingSpacing200,
}))
